import { RefObject, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getIsSheetOpen, getSheetActiveSnapPoint, getSheetKey } from '../sheetSelectors';
import { closeSheet, openSheet, updateSheetActiveSnapPoint } from '../sheetActions';

import { SheetCloseOptions, SheetId, SheetOpenOptions } from '../sheetTypes';

export type UseSheetReturnType = {
    sheetContainerRef: RefObject<HTMLDivElement>;
    sheetRef: RefObject<HTMLDivElement>;
    sheetContentRef: RefObject<HTMLDivElement>;
    sheetId: SheetId;
    sheetKey: string;
    isSheetOpen: boolean;
    activeSnapPoint: number;
    dispatchOpenSheet: (options?: SheetOpenOptions) => void;
    dispatchCloseSheet: (options?: SheetCloseOptions) => void;
    dispatchUpdateActiveSnapPoint: (activeSnapPoint: number) => void;
};

const useSheet = (
    sheetId: SheetId,

    // Include instanceId if multiple instances of the same sheet are used (e.g. element-specific sheets)
    instanceId?: string,
): UseSheetReturnType => {
    const sheetContainerRef = useRef<HTMLDivElement>(null);
    const sheetRef = useRef<HTMLDivElement>(null);
    const sheetContentRef = useRef(null);
    const sheetKey = getSheetKey(sheetId, instanceId);

    const isSheetOpen = useSelector(getIsSheetOpen(sheetId, instanceId));
    const activeSnapPoint = useSelector(getSheetActiveSnapPoint(sheetId, instanceId));

    const dispatch = useDispatch();

    const dispatchOpenSheet = (options?: SheetOpenOptions) => dispatch(openSheet(sheetId, instanceId, options));
    const dispatchCloseSheet = (options?: SheetCloseOptions) => dispatch(closeSheet(sheetId, instanceId, options));

    const dispatchUpdateActiveSnapPoint = (activeSnapPoint: number) =>
        dispatch(updateSheetActiveSnapPoint(activeSnapPoint, sheetId, instanceId));

    return {
        sheetContainerRef,
        sheetRef,
        sheetContentRef,
        sheetId,
        sheetKey,
        isSheetOpen,
        activeSnapPoint,
        dispatchOpenSheet,
        dispatchCloseSheet,
        dispatchUpdateActiveSnapPoint,
    };
};

export default useSheet;
